import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useBackHomePath } from "@/lib/useBackHomePath";
import { Layout } from "@/components/Layout";
import { ArrowLeft, MessageSquareText, Filter, Calendar, Users } from "lucide-react";

const CATEGORY_META = {
    very_good: { label: "Shumë mirë", chip: "bg-sage/10 text-sage border-sage/30" },
    good: { label: "Mirë", chip: "bg-sage/5 text-sage border-sage/20" },
    neutral: { label: "Neutral", chip: "bg-slate-100 text-slate-700 border-slate-200" },
    bad: { label: "Keq", chip: "bg-amber-50 text-amber-700 border-amber-200" },
    very_bad: { label: "Shumë keq", chip: "bg-terracotta/10 text-terracotta border-terracotta/30" },
    warning: { label: "Vërejtje", chip: "bg-terracotta/15 text-terracotta border-terracotta/40" },
};

const monthStart = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function TeacherComments() {
    const [classes, setClasses] = useState([]);
    const [students, setStudents] = useState([]);
    const [comments, setComments] = useState([]);
    const [classId, setClassId] = useState("");
    const [studentId, setStudentId] = useState("");
    const [dateFrom, setDateFrom] = useState(monthStart());
    const [dateTo, setDateTo] = useState(todayISO());
    const [loading, setLoading] = useState(true);
    const backPath = useBackHomePath();

    useEffect(() => {
        (async () => {
            const [c, s] = await Promise.all([api.get("/classes"), api.get("/teacher/students")]);
            setClasses(c.data); setStudents(s.data);
        })();
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const params = {};
            if (classId) params.class_id = classId;
            if (studentId) params.student_id = studentId;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            try {
                const r = await api.get("/teacher/comments", { params });
                setComments(r.data.comments || []);
            } finally { setLoading(false); }
        })();
    }, [classId, studentId, dateFrom, dateTo]);

    const filteredStudents = useMemo(() => classId ? students.filter((s) => s.class_id === classId) : students, [classId, students]);

    const summary = useMemo(() => {
        const m = {};
        for (const c of comments) m[c.category] = (m[c.category] || 0) + 1;
        return m;
    }, [comments]);

    return (
        <Layout>
            <Link to={backPath} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Komentet e mia</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Komentet</h1>
                <p className="text-slate-600 mt-2 text-sm">Filtroni komentet tuaja sipas klasës, nxënësit ose periudhës</p>
            </div>

            {/* Filters */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1 mb-1"><Users className="w-3 h-3" /> Klasa</label>
                    <select value={classId} onChange={(e) => { setClassId(e.target.value); setStudentId(""); }} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-3" data-testid="comments-filter-class">
                        <option value="">Të gjitha klasat</option>
                        {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1 mb-1"><Users className="w-3 h-3" /> Nxënësi</label>
                    <select value={studentId} onChange={(e) => setStudentId(e.target.value)} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-3" data-testid="comments-filter-student">
                        <option value="">Të gjithë nxënësit</option>
                        {filteredStudents.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1 mb-1"><Calendar className="w-3 h-3" /> Nga data</label>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-3 font-mono" data-testid="comments-filter-from" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1 mb-1"><Calendar className="w-3 h-3" /> Deri më</label>
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-3 font-mono" data-testid="comments-filter-to" />
                </div>
            </div>

            {/* Summary chips */}
            {comments.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2 text-[11px]">
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-oxford text-white font-bold">{comments.length} totale</span>
                    {Object.entries(summary).map(([cat, n]) => (
                        <span key={cat} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border font-semibold ${CATEGORY_META[cat]?.chip || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                            {CATEGORY_META[cat]?.label || cat}: <strong>{n}</strong>
                        </span>
                    ))}
                </div>
            )}

            {/* Comments list */}
            {loading ? <div className="text-slate-500">Duke ngarkuar…</div> : comments.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
                    <MessageSquareText className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                    <div className="font-heading text-xl font-bold text-slate-800">Pa komente në këtë filtër</div>
                    <p className="text-sm text-slate-600 mt-1">Provo periudhë tjetër ose hiq filtrat.</p>
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="divide-y divide-slate-200">
                        {comments.map((c) => {
                            const meta = CATEGORY_META[c.category] || { label: c.category, chip: "bg-slate-100 text-slate-700 border-slate-200" };
                            return (
                                <div key={c.id} className="p-4 sm:p-5" data-testid={`tcomment-${c.id}`}>
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-bold ${meta.chip}`}>{meta.label}</span>
                                        <Link to={`/teacher/student/${c.student_id}`} className="text-sm font-bold text-slate-900 hover:text-oxford">{c.student_name}</Link>
                                        <span className="text-xs text-slate-500">• {c.class_name}</span>
                                        <span className="text-xs text-slate-500">• {c.subject_name}</span>
                                        <span className="ml-auto text-[11px] text-slate-400 font-mono">{c.created_at?.slice(0, 10)}</span>
                                    </div>
                                    {c.text && <p className="text-sm text-slate-700 leading-relaxed pl-1">{c.text}</p>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </Layout>
    );
}
